/* ============================================================================
   Timezone arithmetic.

   Every time in Encore is stored as a UTC instant plus the IANA zone it was
   entered in. A show at 20:00 in Berkeley is one instant; rendering it in
   Auckland must still say 20:00, and the day it belongs to is the Berkeley day.

   JavaScript has no way to construct "20:00 on 6 Nov, in America/Los_Angeles"
   directly — `new Date(y, m, d, h, min)` uses the *browser's* zone. So the
   instant is found by guessing and correcting against the zone's real offset.
   ============================================================================ */

/** The zone's UTC offset in milliseconds at a given instant. */
export function zoneOffsetMs(instant: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instant))

  const field = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? '0')

  // What the wall clock reads in that zone, expressed as if it were UTC. The
  // gap between that and the true instant is the offset.
  const asUtc = Date.UTC(
    field('year'),
    field('month') - 1,
    field('day'),
    field('hour'),
    field('minute'),
    field('second'),
  )
  return asUtc - instant
}

/**
 * Builds the UTC instant for a wall-clock time in a given zone.
 *
 * Corrects twice. One pass is wrong across a DST boundary — the offset used to
 * adjust the guess may not be the offset in force at the corrected instant,
 * which is exactly the hour a soundcheck would silently move.
 */
export function zonedTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): Date {
  const guess = Date.UTC(year, month - 1, day, hour, minute)
  const firstPass = guess - zoneOffsetMs(guess, timeZone)
  const secondPass = guess - zoneOffsetMs(firstPass, timeZone)
  return new Date(secondPass)
}

/**
 * Takes the calendar day from `day` and the clock time from `time`, resolved in
 * `timeZone`.
 *
 * The direct port of `ShowWriter.combine(day:time:in:)`. Both inputs are read
 * in the *browser's* zone for their component parts, exactly as the Swift reads
 * them in the device's — the parts are what the user typed, not an instant.
 */
export function combineDayAndTime(day: Date, time: Date, timeZone: string): Date {
  return zonedTimeToUtc(
    day.getFullYear(),
    day.getMonth() + 1,
    day.getDate(),
    time.getHours(),
    time.getMinutes(),
    timeZone,
  )
}

/** "2026-11-06" + "20:00" + zone → instant. What the forms actually hand over. */
export function fromDateAndTimeStrings(
  dateValue: string,
  timeValue: string,
  timeZone: string,
): Date | null {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue)
  const timeMatch = /^(\d{2}):(\d{2})/.exec(timeValue)
  if (!dateMatch || !timeMatch) return null

  return zonedTimeToUtc(
    Number(dateMatch[1]),
    Number(dateMatch[2]),
    Number(dateMatch[3]),
    Number(timeMatch[1]),
    Number(timeMatch[2]),
    timeZone,
  )
}

/** Instant → "2026-11-06" as read in `timeZone`. For populating a date input. */
export function toDateInputValue(date: Date, timeZone?: string | null): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  }).format(date)
}

/** Instant → "20:00" as read in `timeZone`. For populating a time input. */
export function toTimeInputValue(date: Date, timeZone?: string | null): string {
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    ...(timeZone ? { timeZone } : {}),
  }).format(date)
}

export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

/** Every zone the browser knows, for the venue timezone picker. */
export function allTimeZones(): string[] {
  const supported = (
    Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf
  if (typeof supported === 'function') return supported('timeZone')
  // Older engines: the browser zone is better than nothing.
  return [browserTimeZone()]
}
